import { imagePdfAndDocExtensions } from '@common/utils/fileExtensions';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IPatientContactInfoDraft } from '../../../../../../common/validation/patientContactInfo';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { echelonOptions } from '../../../../../../common/validation/customControls/mentalHealth';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { CheckboxField } from '../../../../fields/Checkbox';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { WarningToAddOrganizationStamp } from '../../utils/v1/WarningToAddOrganizationStamp';

export interface ICustomControlDataGgzv {
    anamnese: string | null;
    besproken_met_patient: string | null;
    contactgegevens_patient: IPatientContactInfoDraft;
    context: string | null;
    echelon: 'Generalistische Basis GGZ' | 'Specialistische GGZ' | null;
    huidig_beleid: string | null;
    klacht_of_hulpvraag: string | null;
    ook_bekend_bij: string | null;
    procedurevoorstel: string | null;
    relevante_comorbiditeit: string | null;
    risicovol_gedrag: string | null;
    suicidaliteit_en_psychose: string | null;
    toestemming: boolean;
    vermoeden_van_dsm_stoornis: string | null;
}

interface IInnerGgzvV1RequestAddEditState {
    buttonDisabled?: boolean;
    hasOrganizationStamp?: boolean;
    proceedWithoutFiles?: boolean;
    showProceedWithoutFiles?: boolean;
}

export function GgzvV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    return <InnerGgzvV1RequestAddEdit {...props} />;
}

class InnerGgzvV1RequestAddEdit extends Component<ICustomControlRequestAddEditProps, IInnerGgzvV1RequestAddEditState> {
    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                <Form onSubmit={async () => null}>
                    {this.state.showProceedWithoutFiles && (
                        <ProceedWithoutFiles
                            entity={ProcessEntity.request}
                            onNo={async () =>
                                await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                            }
                            onYes={async () => {
                                await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                                await this.saveRequest('final');
                            }}
                            serviceType={this.props.service.serviceType}
                        />
                    )}

                    <CareProviderSelector
                        {...this.props}
                        entity={this.props.request}
                    />

                    <ServiceOrganizationProvider
                        errors={this.props.errors}
                        initialOrganizationId={this.props.request.selectedDiagnosticReportProviderOrganizationId}
                        initialPractitionerId={this.props.request.selectedDiagnosticReportProviderPractitionerId}
                        onChange={async (organizationId, practitionerId) => {
                            await this.props.handleChange(
                                'selectedDiagnosticReportProviderOrganizationId',
                                organizationId
                            );
                            await this.props.handleChange(
                                'selectedDiagnosticReportProviderPractitionerId',
                                practitionerId
                            );
                        }}
                        onDoValidate={async () =>
                            await this.props.validateOne('selectedDiagnosticReportProviderOrganizationId')
                        }
                        roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                        searchInNetwork
                        serviceProviderServiceId={this.props.service.id}
                        serviceType={this.props.service.serviceType}
                    />

                    <FormDivider />

                    <HeaderDivider text={translateKey('procedure.procedure')} />

                    <AssetDropzoneWithActions
                        acceptedFiles={imagePdfAndDocExtensions}
                        assets={this.props.request.assets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={this.props.isAutoSaveActive}
                        onChange={async (assets) => await this.props.handleChange('assets', assets)}
                        onUploading={async (isUploading) => await this.setState({ buttonDisabled: isUploading })}
                        processActivityId={this.props.request.id}
                        service={this.props.service}
                        showUseExistingExamination={!this.props.request.previousRequestId}
                        updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                    />

                    <MedicationsAddEdit
                        entity={ProcessEntity.request}
                        entityId={this.props.request.id}
                        errors={this.props.errors}
                        handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                            await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                        }
                        onChange={async (medications: IMedication[]) =>
                            await this.props.handleChange('requestMedications', medications)
                        }
                        patientUsesMedication={this.props.request.patientUsesMedication}
                        validateMedications={async () => await this.props.validateOne('requestMedications')}
                        validatePatientUsesMedication={async () =>
                            await this.props.validateOne('patientUsesMedication')
                        }
                    />

                    <ProfessionalSummaryData
                        entity={ProcessEntity.request}
                        entityId={this.props.request.id}
                        errors={this.props.errors}
                        handleChangeAllergies={async (allergies) =>
                            await this.props.handleChange('allergies', allergies)
                        }
                        handleChangeEpisodes={async (episodesAndJournal) =>
                            await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                        }
                        handleChangeLabResults={async (labResults) =>
                            await this.props.handleChange('requestLabResults', labResults)
                        }
                        validateEpisodesAndJournals={async () =>
                            await this.props.validateOne('requestEpisodesAndJournal')
                        }
                    />

                    {this.addEditForm()}

                    <WarningToAddOrganizationStamp
                        careProviderOrganizationId={this.props.request.draftCareProviderOrganizationId}
                        careProviderPractitionerId={this.props.request.draftCareProviderPractitionerId}
                        employmentId={this.props.employmentId}
                        onSaveRequest={this.props.onSaveRequest}
                        setHasOrganizationStamp={async (value) => await this.setState({ hasOrganizationStamp: value })}
                        validateRequest={this.props.validateRequest}
                    />

                    <FormDivider />

                    <PatientContactAddEdit {...this.props} />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertRequestChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveRequest('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled || !this.state.hasOrganizationStamp}
                                saveSend={async () => {
                                    await this.saveRequest('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </NewOrEditRequestProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'anamnese',
                    item: this.props.request.customControlData,
                    errors: this.props.errors,
                    customControlData: true,
                    onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                }}
            >
                <HeaderDivider text={'Verwijzing'} />

                <RadioGroupField
                    automation="echelon"
                    fieldKey="echelon"
                    isRequired
                    label="Echelon"
                    labels={echelonOptions}
                    options={echelonOptions}
                />
                <TextAreaField
                    automation="complaint"
                    fieldKey="klacht_of_hulpvraag"
                    label="Klacht of hulpvraag"
                    placeholder="Klacht of hulpvraag van de patiënt..."
                />
                <TextAreaField
                    automation="disorder"
                    fieldKey="vermoeden_van_dsm_stoornis"
                    isRequired
                    label="Vermoeden van DSM stoornis"
                    placeholder="Omschrijf (het vermoeden van) een psychische stoornis..."
                />
                <TextAreaField
                    automation="currentGuidelines"
                    fieldKey="huidig_beleid"
                    label="Huidig beleid"
                    placeholder="Ingestelde (of gestaakte) behandeling of medicatie en de door u uit te voeren controles..."
                />
                <TextAreaField
                    automation="discussedWithPatient"
                    fieldKey="besproken_met_patient"
                    label="Besproken met patiënt"
                    placeholder="Wat is besproken met patiënt, eerdere adviezen en voorbereidingen van de POH-GGZ..."
                />
                <TextAreaField
                    automation="proceduralProposal"
                    fieldKey="procedurevoorstel"
                    label="Procedurevoorstel"
                    placeholder="Geef aan hoe u betrokken wilt blijven..."
                />

                <FormDivider />
                <HeaderDivider text={'Anamnese'} />

                <TextAreaField
                    automation="anamnese"
                    fieldKey="anamnese"
                    label="Anamnese"
                    placeholder="Aard, ontstaan, duur, beloop, recente behandelingen en effect..."
                />
                <TextAreaField
                    automation="context"
                    fieldKey="context"
                    label="Context"
                    placeholder="Psychiatrische voorgeschiedenis, familieanamnese, psychosociale omstandigheden..."
                />
                <TextAreaField
                    automation="comorbidity"
                    fieldKey="relevante_comorbiditeit"
                    label="Relevante comorbiditeit"
                    placeholder="Somatische problematiek..."
                />
                <TextAreaField
                    automation="suicidality"
                    fieldKey="suicidaliteit_en_psychose"
                    label="Suïcidaliteit en psychose"
                    placeholder="Eventuele suïcidaliteit of psychose in het verleden..."
                />
                <TextAreaField
                    automation="riskyBehaviour"
                    fieldKey="risicovol_gedrag"
                    label="Risicovol gedrag"
                    placeholder="Beschrijf je bevindingen, indien bekend. Vermeld ook verslavingsgedrag zoals gokken..."
                />
                <TextAreaField
                    automation="otherCareProvider"
                    fieldKey="ook_bekend_bij"
                    label="Ook bekend bij"
                    placeholder="Andere zorgverleners waarbij patiënt in behandeling is/was..."
                />

                <FormSection type="referral">
                    <HeaderDivider text="Toestemming voor de verwijzing" />
                    <InputControl isFormControl>
                        <CheckboxField
                            automation="permissionReferral"
                            fieldKey="toestemming"
                            isRequired
                            label=""
                            text="De patiënt geeft toestemming voor de verwijzing"
                        />
                    </InputControl>
                </FormSection>
            </FormContextProvider>
        );
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutFiles &&
            saveType === 'final' &&
            this.props.request.assets.length === 0 &&
            (!this.props.request.customControlData.files || this.props.request.customControlData.files.length === 0)
        ) {
            return await this.setState({ showProceedWithoutFiles: true });
        }

        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await this.props.onSave(requestId);
        if (this.props.reloadProcess) {
            await this.props.reloadProcess(ReloadEpisode.reloadAll);
        }
        if (this.props.reloadPatient) {
            await this.props.reloadPatient();
        }
    }
}

export function ggzvV1InitializeCustomControlData(): ICustomControlDataGgzv {
    return {
        anamnese: null,
        context: null,
        relevante_comorbiditeit: null,
        suicidaliteit_en_psychose: null,
        risicovol_gedrag: null,
        ook_bekend_bij: null,
        echelon: null,
        klacht_of_hulpvraag: null,
        vermoeden_van_dsm_stoornis: null,
        huidig_beleid: null,
        besproken_met_patient: null,
        procedurevoorstel: null,
        toestemming: false,
        contactgegevens_patient: {
            emailadres: null,
            mobileNumber: null,
            homePhoneNumber: null,
        },
    };
}
